<template>
  <div class="apt-reminder-page">
    <!-- Page Header + Controls (single row) -->
    <div class="apt-header">
      <h2 class="apt-title">
        <i class="fa fa-calendar-check-o"></i>
        Appointment Reminder
      </h2>
      <div class="apt-header-controls">
        <div class="date-picker-wrapper">
          <i class="fa fa-calendar date-icon"></i>
          <DatePicker
            lang="en"
            placeholder="Select a date..."
            input-class="date-input"
            v-model="aptDate"
            :format="dateFormatStr"
            class="custom-datepicker"
          />
        </div>
        <button class="change-date-btn" @click="changeDate">
          <i class="fa fa-refresh"></i>
          Load Appointments
        </button>
        <span class="apt-count" v-if="readyToLoad && totalAppointments > 0">{{ totalAppointments }} appointments</span>
        <button class="settings-btn" @click="setAptReminderSettings" title="Settings">
          <i class="fa fa-cog"></i>
        </button>
      </div>
    </div>

    <ErrorDisplay/>

    <!-- Loading indicator -->
    <div v-if="appointmentsLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Loading appointments...</p>
    </div>

    <!-- Provider Summary -->
    <div class="provider-summary-wrapper" v-if="!appointmentsLoading && readyToLoad && providerSummary && providerSummary.length > 0">
      <div class="provider-summary-label">
        <i class="fa fa-stethoscope"></i>
        Provider Overview
      </div>
      <div class="provider-summary-cards">
        <div class="provider-card" v-for="provider in providerSummary" :key="provider.name">
          <div class="provider-name">
            <i class="fa fa-user-md"></i>
            {{ provider.name }}
          </div>
          <div class="provider-stats">
            <span class="stat-total">{{ provider.total }} patients</span>
            <span :class="provider.confirmed === 0 ? 'stat-none-confirmed' : 'stat-confirmed'"><i :class="provider.confirmed === 0 ? 'fa fa-times-circle' : 'fa fa-check-circle'"></i> {{ provider.confirmed }} confirmed</span>
            <span class="stat-unconfirmed" v-if="provider.unconfirmed > 0">
              <i class="fa fa-exclamation-circle"></i> {{ provider.unconfirmed }} unconfirmed
            </span>
          </div>
          <div class="provider-status-breakdown" v-if="provider.statusBreakdown && provider.statusBreakdown.length > 0">
            <span class="status-item" v-for="s in provider.statusBreakdown" :key="s.status">{{ s.status }}: {{ s.count }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div class="empty-state" v-if="!appointmentsLoading && readyToLoad && (!opsOrdered || opsOrdered.length === 0)">
      <div class="empty-icon">
        <i class="fa fa-calendar-o"></i>
      </div>
      <h4>No Appointments Found</h4>
      <p>There are no appointments scheduled for this date</p>
    </div>

    <!-- Legend -->
    <div class="apt-legend" v-if="!appointmentsLoading && readyToLoad && opsOrdered && opsOrdered.length > 0">
      <div class="legend-item">
        <span class="legend-color confirmed"></span>
        <span class="legend-label">Confirmed</span>
      </div>
      <div class="legend-item">
        <span class="legend-color unconfirmed"></span>
        <span class="legend-label">Unconfirmed</span>
      </div>
    </div>

    <!-- Appointments Grid -->
    <div class="apt-grid-container" v-if="!appointmentsLoading && readyToLoad && opsOrdered && opsOrdered.length > 0">
      <div class="apt-columns">
        <div
          class="apt-column"
          v-for="(op, opIndex) in opsOrdered"
          :key="op[0].OpName"
        >
          <!-- Column Header -->
          <div class="column-header">
            <div class="column-icon">
              <i class="fa fa-medkit"></i>
            </div>
            <h5 class="column-title">{{ op[0].OpName }}</h5>
            <span class="column-count">{{ op.length }}</span>
          </div>

          <!-- Appointment Cards -->
          <div class="apt-cards-wrapper">
            <button class="scroll-arrow scroll-arrow-up" v-if="columnCanScrollUp[opIndex]" @click="scrollColumnUp(opIndex)">
              <i class="fa fa-chevron-up"></i>
            </button>
            <div class="apt-cards-container" :ref="'cards-' + opIndex" @scroll="checkScroll(opIndex)">
              <div
                class="apt-card"
                :class="{ 'confirmed': isAptConfirmed(row.DefNum), 'unconfirmed': !isAptConfirmed(row.DefNum) }"
                v-for="row in opsOrdered[opIndex]"
                :key="row.AptNum"
                @click="handleClick($event, row)"
              >
                <div class="apt-card-accent" :class="{ 'confirmed': isAptConfirmed(row.DefNum) }"></div>
                <div class="apt-card-content">
                  <div class="apt-time">
                    <i class="fa fa-clock-o"></i>
                    {{ row.AptDateTime | timeformatwithtz(allSettings.GENERAL_TIMEZONE.PropertyValue) }}
                  </div>
                  <div class="apt-patient">{{ row.Patient }}</div>
                  <div class="apt-procedures" v-if="row.Procedures">{{ row.Procedures }}</div>
                  <div class="apt-note" v-if="row.Note">{{ row.Note }}</div>
                  <div class="apt-status" v-if="!isAptConfirmed(row.DefNum)">
                    <span class="status-badge unconfirmed">
                      <i class="fa fa-exclamation-circle"></i>
                      {{ row.OpenDentalAptConfirmStatus }}
                    </span>
                  </div>
                  <div class="apt-status" v-else>
                    <span class="status-badge confirmed">
                      <i class="fa fa-check-circle"></i>
                      Confirmed
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="scroll-fade" v-if="columnCanScroll[opIndex]"></div>
            <div class="scroll-more-chip" v-if="columnCanScroll[opIndex]" @click="scrollColumn(opIndex)">
              <i class="fa fa-chevron-down"></i> scroll for more
            </div>
            <button class="scroll-arrow scroll-arrow-down" v-if="columnCanScroll[opIndex]" @click="scrollColumn(opIndex)">
              <i class="fa fa-chevron-down"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <slideout-panel></slideout-panel>

    <b-modal
      ref="aptblock"
      id="aptblock"
      title="Appointment Details"
      body-class="bg-white mb-0"
      header-class="apt-modal-header"
    >
      <b-row v-if="this.selectedAptBlock">
        <b-col>
          <p><label class="mt-2 mb-1">Patient: {{selectedAptBlock.Patient}}</label></p>
          <p><label class="mt-2 mb-1">AptNum: {{selectedAptBlock.AptNum}}</label></p>
          <p><label class="mt-2 mb-1">Procedures: {{selectedAptBlock.Procedures}}</label></p>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable */

import Vue from 'vue';
import { mapState, mapActions, mapMutations } from 'vuex';
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import AptReminderSettings from './components/AptReminderSettings';
import mixin from '../../mixin.js';
import Widget from '@/components/Widget/Widget';
import moment from 'moment-timezone';
import DatePicker from "vue2-datepicker";
import AptReminderSidebar from './components/AptReminderSidebar';

export default {
  name: "AptReminder",
  components: { ErrorDisplay, AptReminderSettings, Widget, DatePicker},
  mixins: [mixin],

  created() {
    this.init();
  },

  mounted() {    
  },

  data() {
    return {
      aptDate: null,
      selectedAptBlock: null,
      columnCanScroll: {},
      columnCanScrollUp: {},
      appointmentsLoading: true
    };
  },

  methods: {
    //...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),  
    ...mapActions('aptreminder', ['getAptReminderData']),
    
    async init() {
      this.appointmentsLoading = true;
      this.aptDate = this.$route.query.d ? new Date(this.$route.query.d + 'T00:00:00') : new Date();
      let aptDate = moment(this.aptDate).format('YYYY-MM-DD');
      await this.getAptReminderData({siteID: this.siteID, aptDate: aptDate});
      this.appointmentsLoading = false;
      this.$nextTick(() => this.checkAllScrolls());
    },

    async changeDate() {
      if(this.aptDate) {
        this.appointmentsLoading = true;
        let aptDate = moment(this.aptDate).format('YYYY-MM-DD');
        await this.getAptReminderData({siteID: this.siteID, aptDate: aptDate});
        this.appointmentsLoading = false;
        this.$nextTick(() => this.checkAllScrolls());
      } else {
        this.$toast.error('Please select a date first');
        return;
      }
    },

    checkAllScrolls() {
      if(!this.opsOrdered) return;
      for(let i = 0; i < this.opsOrdered.length; i++) {
        this.checkScroll(i);
      }
    },

    checkScroll(opIndex) {
      let refs = this.$refs['cards-' + opIndex];
      if(!refs || !refs.length) return;
      let el = refs[0];
      let hasMore = el.scrollHeight - el.scrollTop - el.clientHeight > 10;
      let hasScrolledDown = el.scrollTop > 10;
      Vue.set(this.columnCanScroll, opIndex, hasMore);
      Vue.set(this.columnCanScrollUp, opIndex, hasScrolledDown);
    },

    scrollColumn(opIndex) {
      let refs = this.$refs['cards-' + opIndex];
      if(!refs || !refs.length) return;
      let el = refs[0];
      el.scrollBy({ top: 150, behavior: 'smooth' });
    },

    scrollColumnUp(opIndex) {
      let refs = this.$refs['cards-' + opIndex];
      if(!refs || !refs.length) return;
      let el = refs[0];
      el.scrollBy({ top: -150, behavior: 'smooth' });
    },

    async handleClick(event, apt) {
      //console.dir(apt);
      event.preventDefault();
      var _this = this;
      try {
        const handle = this.$showPanel({
          component : AptReminderSidebar,
          openOn: 'right',
          width: 1850,
          disableBgClick: true,
          disableEscClick: true,
          props: {
            apt: apt
          }
        });
        
        handle.promise.then(async(result) => {
          //console.log(`Got result: ${JSON.stringify(result, null, 4)}`);
        });
      } catch(err) {        
        console.log(err);
      } finally {        
      }
    },

    hideModal(ref) {
      this.$refs[ref].hide();
    },

    showModal(e, ref, data) {      
      e.preventDefault();
      //console.log(`show modal called with temp: ${e}, ref: ${ref} and data: ${data}`);
      this.selectedAptBlock = data;
      Vue.nextTick(() => {
        this.$bvModal.show(ref);
      });      
    },

    getAptBlockHeight(aptLength) {
      let height = 34 * (aptLength/10);
      if (height <= 120)
        return 120;
      else
        return height;
    },

    getStyle(aptLength) {
      let height = this.getAptBlockHeight(aptLength);
      return `margin-bottom:5px;height:${height}px;`;
    },

    isAptConfirmed(defNum) {
      if(this.defNumConfirmStatus)
        return this.defNumConfirmStatus.includes(defNum);
    },

    getColStyle(defNum) {
      let style;
      if(this.isAptConfirmed(defNum))
        style = `background-color:#aaf0d1;margin-left:5px;margin-right:5px;margin-top:5px;`;
      else
        style = `background-color:#F2F2F2;margin-left:5px;margin-right:5px;margin-top:5px;`;
      return style;
    },

    async setAptReminderSettings(event, props) {    
      event.preventDefault();
      //Vue.set(this.loadingStatus, true);
      var _this = this;
      try {
        const handle = this.$showPanel({
          component : AptReminderSettings,
          openOn: 'right',
          width: 1850,
          disableBgClick: true,
          disableEscClick: true          
        });
        handle.promise.then(async(result) => {
          //console.log(`Got result: ${JSON.stringify(result, null, 4)}`);
          /* I shoudn't have to do the below but somehow this was the only way to force the chart to refresh */
          //this.refreshKey = !this.refreshKey;
        });      
      } catch(err) {
        //Vue.set(this.loadingStatus, false);
        console.log(err);
      } finally {
        //console.log("finally");
      }
    },

  },

  computed: {
    ...mapState({
      allAptReminderData: state => state.aptreminder.aptReminderData
    }),

    readyToLoad() {
      if(this.allSettings && this.allAptReminderData)
        return true;
      else
        return false;
    },

    totalAppointments() {
      if(this.opsOrdered) {
        return this.opsOrdered.reduce((total, op) => total + op.length, 0);
      }
      return 0;
    },

    aptReminderData() {
      if(this.allAptReminderData) {
        return this.allAptReminderData[0];
      }
    },

    providerSummary() {
      if(!this.aptReminderData || this.aptReminderData.length === 0) return [];

      let grouped = _.groupBy(this.aptReminderData, 'Provider');
      return Object.keys(grouped).sort().map(providerName => {
        let apts = grouped[providerName];
        let confirmed = apts.filter(a => this.isAptConfirmed(a.DefNum)).length;
        let unconfirmed = apts.length - confirmed;

        // Group unconfirmed by their status name
        let unconfirmedApts = apts.filter(a => !this.isAptConfirmed(a.DefNum));
        let statusCounts = _.countBy(unconfirmedApts, 'OpenDentalAptConfirmStatus');
        let statusBreakdown = Object.keys(statusCounts).map(status => ({
          status: status,
          count: statusCounts[status]
        }));

        return {
          name: providerName,
          total: apts.length,
          confirmed: confirmed,
          unconfirmed: unconfirmed,
          statusBreakdown: statusBreakdown
        };
      });
    },

    aptReminderPaidFor() {
      if(this.allSettings) {
        if(this.allSettings.APPT_REMINDER_PAID_FOR && this.allSettings.APPT_REMINDER_PAID_FOR.PropertyValue === "TRUE")
          return true;
        else
          return false;
      }
    },

    defNumConfirmStatus() {
      let temp = [];

      if(this.allAptReminderData) {
        if(this.allAptReminderData[1])
          temp.push(this.allAptReminderData[1][0].DefNum);
      }

      if(this.aptReminderSettings) {
        let defNums = _.map(this.aptReminderSettings['confirmationStatusToIgnore'], 'ODDefNum');
        //console.log(JSON.stringify(defNums));
        temp = [...temp, ...defNums];
      }
      return temp;
    },

    aptReminderSettings() {
      if(this.allSettings) {
        let settings = JSON.parse(this.allSettings['APPT_REMINDER_SETTINGS'].PropertyValue);
        for (var prop in settings) {
          //console.log(`prop: ${prop}`);                    
          Vue.set(this, prop, settings[prop]);
        }
        return settings; 
      }
    },

    ops() {
      if(this.aptReminderData) {
        return _.groupBy(this.aptReminderData, 'OpName');  
      }
    },

    opsOrdered() {
      if(this.ops) {
        return _.orderBy(this.ops, 'OperatoryOrder');
      }
    },

    lg() {
      if(this.opsOrdered && this.opsOrdered.length) {        
        if(this.opsOrdered.length < 3)
          return 5;
        else if(this.opsOrdered.length === 3)
          return 3;
        else if(this.opsOrdered.length > 3)        
          return 2;
      }      
    },

    xs() {
      if(this.opsOrdered && this.opsOrdered.length) {        
        if(this.opsOrdered.length < 3)
          return 5;
        else if(this.opsOrdered.length === 3)
          return 3;
        else if(this.opsOrdered.length > 3)        
          return 2;
      }
    },

  }
};

</script>


<style lang='scss' scoped>
// Appointment Reminder Page - Modern Design

.apt-reminder-page {
  padding: 0;
  max-width: 100%;
}

// Header + Controls (single row)
.apt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
  background: white;
  border-radius: 12px;
  padding: 12px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;

  .apt-title {
    font-size: 20px;
    font-weight: 700;
    color: #2d3748;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    white-space: nowrap;

    .fa {
      color: #3abf94;
      font-size: 20px !important;
      padding: 0 !important;
    }
  }

  .apt-header-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .date-picker-wrapper {
    display: flex;
    align-items: center;
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    padding: 2px 10px;
    transition: all 0.2s ease;
    min-width: 200px;

    &:focus-within {
      border-color: #3abf94;
      box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.15);
    }

    .date-icon {
      color: #718096;
      font-size: 14px !important;
      padding: 0 !important;
      margin-right: 8px;
    }

    .custom-datepicker {
      flex: 1;

      ::v-deep .mx-input-wrapper {
        .date-input {
          border: none !important;
          background: transparent !important;
          padding: 8px 6px !important;
          font-size: 14px !important;
          color: #2d3748 !important;
          outline: none !important;
          box-shadow: none !important;
          width: 100%;

          &::placeholder {
            color: #a0aec0;
          }
        }
      }

      ::v-deep .mx-datepicker {
        width: 100%;
      }

      ::v-deep input {
        border: none !important;
        background: transparent !important;
        padding: 8px 6px !important;
        font-size: 14px !important;
        color: #2d3748 !important;
        outline: none !important;
        box-shadow: none !important;
      }
    }
  }

  .change-date-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    background: #3abf94;
    border: none;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(58, 191, 148, 0.3);

    &:hover {
      background: #2d9474;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(58, 191, 148, 0.4);
    }

    &:active {
      transform: translateY(0);
    }

    .fa {
      font-size: 13px !important;
      padding: 0 !important;
    }
  }

  .apt-count {
    background: linear-gradient(135deg, #3abf94, #2d9474);
    color: white;
    padding: 5px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
  }

  .settings-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: white;
    border: 1px solid #e2e8f0;
    color: #718096;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #f7fafc;
      color: #3abf94;
      border-color: #3abf94;
      transform: rotate(45deg);
    }

    .fa {
      font-size: 16px !important;
      padding: 0 !important;
    }
  }
}

// Selected Date Banner
// Empty State
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;

  .empty-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 20px;
    background: #f7fafc;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    .fa {
      font-size: 36px !important;
      padding: 0 !important;
      color: #a0aec0;
    }
  }

  h4 {
    font-size: 18px;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    color: #718096;
    margin-bottom: 0;
  }
}

// Appointments Grid Container
.apt-grid-container {
  margin-bottom: 24px;
}

.apt-columns {
  display: flex;
  gap: 20px;
  overflow-x: auto;
  padding-bottom: 16px;

  // Custom scrollbar
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;

    &:hover {
      background: #94a3b8;
    }
  }
}

// Appointment Column
.apt-column {
  min-width: 300px;
  max-width: 350px;
  flex: 1;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;

  .column-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;

    .column-icon {
      width: 36px;
      height: 36px;
      background: linear-gradient(135deg, #3abf94 0%, #2d9474 100%);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;

      .fa {
        color: white;
        font-size: 16px !important;
        padding: 0 !important;
      }
    }

    .column-title {
      flex: 1;
      font-size: 15px;
      font-weight: 600;
      color: #2d3748;
      margin: 0;
    }

    .column-count {
      background: #edf2f7;
      color: #4a5568;
      font-size: 12px;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 12px;
    }
  }

  .apt-cards-wrapper {
    position: relative;

    .scroll-fade {
      position: absolute;
      bottom: 30px;
      left: 0;
      right: 0;
      height: 60px;
      background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.95));
      pointer-events: none;
      z-index: 1;
      border-bottom-left-radius: 12px;
      border-bottom-right-radius: 12px;
    }

    .scroll-more-chip {
      position: absolute;
      bottom: 36px;
      left: 50%;
      transform: translateX(-50%);
      background: #3abf94;
      color: white;
      font-size: 11px;
      font-weight: 600;
      padding: 4px 14px;
      border-radius: 12px;
      cursor: pointer;
      z-index: 2;
      box-shadow: 0 2px 6px rgba(58, 191, 148, 0.4);
      transition: all 0.2s ease;
      white-space: nowrap;

      &:hover {
        background: #2d9474;
        box-shadow: 0 3px 8px rgba(58, 191, 148, 0.5);
      }

      .fa {
        font-size: 10px !important;
        padding: 0 !important;
        margin-right: 4px;
      }
    }

    .scroll-arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 28px;
      border: none;
      cursor: pointer;
      background: #f0faf6;
      color: #2d9474;
      font-size: 12px;
      transition: all 0.2s ease;

      &:hover {
        background: #d1fae5;
        color: #065f46;
      }

      .fa {
        font-size: 12px !important;
        padding: 0 !important;
      }
    }

    .scroll-arrow-up {
      border-bottom: 1px solid #e2e8f0;
      border-radius: 0;
    }

    .scroll-arrow-down {
      border-top: 1px solid #e2e8f0;
      border-radius: 0 0 12px 12px;
    }
  }

  .apt-cards-container {
    padding: 12px;
    max-height: 600px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 14px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f5f9;
      border-radius: 7px;
    }

    &::-webkit-scrollbar-thumb {
      background: #3abf94;
      border-radius: 7px;
      border: 3px solid #f1f5f9;

      &:hover {
        background: #2d9474;
      }
    }
  }
}

// Appointment Card
.apt-card {
  position: relative;
  background: white;
  border-radius: 10px;
  border: 1px solid #e8e8ef;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
    border-color: #d0d5dd;
  }

  .apt-card-accent {
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: #f59e0b;
    border-radius: 10px 0 0 10px;

    &.confirmed {
      background: linear-gradient(to bottom, #3abf94, #2d9474);
    }
  }

  .apt-card-content {
    padding: 14px 14px 14px 18px;
  }

  .apt-time {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #3abf94;
    margin-bottom: 8px;

    .fa {
      font-size: 12px !important;
      padding: 0 !important;
    }
  }

  .apt-patient {
    font-size: 15px;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 6px;
    line-height: 1.3;
  }

  .apt-procedures {
    font-size: 13px;
    color: #4a5568;
    margin-bottom: 4px;
    line-height: 1.4;
  }

  .apt-note {
    font-size: 12px;
    color: #718096;
    font-style: italic;
    margin-bottom: 8px;
    line-height: 1.4;
  }

  .apt-status {
    margin-top: 10px;

    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;

      .fa {
        font-size: 10px !important;
        padding: 0 !important;
      }

      &.confirmed {
        background: #d1fae5;
        color: #065f46;
      }

      &.unconfirmed {
        background: #fef3c7;
        color: #92400e;
      }
    }
  }

  // Card states
  &.confirmed {
    .apt-card-accent {
      background: linear-gradient(to bottom, #3abf94, #2d9474);
    }
  }

  &.unconfirmed {
    .apt-card-accent {
      background: linear-gradient(to bottom, #f59e0b, #d97706);
    }
  }
}

// Legend
// Provider Summary
.provider-summary-wrapper {
  background: #f0faf6;
  border: 1px solid #c6f0de;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;

  .provider-summary-label {
    font-size: 12px;
    font-weight: 600;
    color: #2d9474;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;

    .fa {
      font-size: 13px !important;
      padding: 0 !important;
      color: #2d9474;
    }
  }

  .provider-summary-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .provider-card {
    background: white;
    border: 1px solid #d1fae5;
    border-radius: 8px;
    padding: 12px 16px;
    min-width: 200px;
    flex: 1;
    max-width: 300px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

    .provider-name {
      font-weight: 600;
      font-size: 14px;
      color: #2d3748;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 8px;

      .fa {
        color: #2d9474;
        font-size: 14px !important;
        padding: 0 !important;
      }
    }

    .provider-stats {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 6px;

      .stat-total {
        font-size: 12px;
        color: #718096;
        font-weight: 500;
        background: #f7fafc;
        padding: 2px 8px;
        border-radius: 4px;
      }

      .stat-confirmed {
        font-size: 12px;
        color: #065f46;
        background: #d1fae5;
        padding: 2px 8px;
        border-radius: 4px;
        font-weight: 500;

        .fa { color: #065f46; font-size: 11px !important; padding: 0 !important; }
      }

      .stat-none-confirmed {
        font-size: 12px;
        color: #991b1b;
        background: #fee2e2;
        padding: 2px 8px;
        border-radius: 4px;
        font-weight: 600;

        .fa { color: #991b1b; font-size: 11px !important; padding: 0 !important; }
      }

      .stat-unconfirmed {
        font-size: 12px;
        color: #92400e;
        background: #fef3c7;
        padding: 2px 8px;
        border-radius: 4px;
        font-weight: 500;

        .fa { color: #92400e; font-size: 11px !important; padding: 0 !important; }
      }
    }

    .provider-status-breakdown {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;

      .status-item {
        font-size: 11px;
        color: #92400e;
        background: #fffbeb;
        padding: 1px 6px;
        border-radius: 3px;
        border: 1px solid #fde68a;
      }
    }
  }
}

.apt-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 16px;
  background: white;
  border-radius: 10px;
  border: 1px solid #e2e8f0;

  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;

    .legend-color {
      width: 12px;
      height: 12px;
      border-radius: 3px;

      &.confirmed {
        background: linear-gradient(135deg, #3abf94, #2d9474);
      }

      &.unconfirmed {
        background: linear-gradient(135deg, #f59e0b, #d97706);
      }
    }

    .legend-label {
      font-size: 13px;
      color: #4a5568;
      font-weight: 500;
    }
  }
}

// Responsive Design
@media (max-width: 991px) {
  .apt-columns {
    flex-direction: column;
  }

  .apt-column {
    min-width: 100%;
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .apt-header {
    flex-direction: column;
    align-items: stretch;

    .apt-title {
      justify-content: center;
    }
  }

  .apt-header {
    .apt-header-controls {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
    }

    .date-picker-wrapper {
      min-width: 100%;
    }

    .change-date-btn {
      justify-content: center;
      width: 100%;
    }
  }

  .apt-legend {
    flex-direction: column;
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .apt-header {
    .apt-title {
      font-size: 20px;

      .fa {
        font-size: 20px !important;
      }
    }
  }

  .apt-card {
    .apt-card-content {
      padding: 12px 12px 12px 16px;
    }

    .apt-patient {
      font-size: 14px;
    }

    .apt-procedures {
      font-size: 12px;
    }
  }
}
</style>

